// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { type AttentionThresholdEntity } from '@/account/api/types';
import type { AttentionReason } from '@/constants/attention';

/**
 * Display copy for configurable attention thresholds, shared by the
 * organization settings table and the audit trail rendering.
 */

interface AttentionThresholdReasonCopy {
  label: string;
  reasonLabel: string;
}

const ATTENTION_THRESHOLD_ENTITY_LABELS: Record<
  AttentionThresholdEntity,
  string
> = {
  outbound_shipment: 'Outbound shipment',
  inbound_shipment: 'Inbound shipment',
};

const ATTENTION_THRESHOLD_REASON_COPY = new Map<
  string,
  AttentionThresholdReasonCopy
>([
  [
    'outbound_shipment:open_not_shipped',
    {
      label: 'Outbound shipment not shipped',
      reasonLabel: 'Created, not yet shipped',
    },
  ],
  [
    'outbound_shipment:shipped_not_delivered',
    {
      label: 'Outbound shipment not delivered',
      reasonLabel: 'Shipped, not yet delivered',
    },
  ],
  [
    'inbound_shipment:open_not_shipped',
    {
      label: 'Inbound shipment not shipped',
      reasonLabel: 'Created, not yet shipped',
    },
  ],
  [
    'inbound_shipment:shipped_not_delivered',
    {
      label: 'Inbound shipment not delivered',
      reasonLabel: 'Shipped, not yet delivered',
    },
  ],
  [
    'inbound_shipment:delivered_not_received',
    {
      label: 'Inbound shipment not received',
      reasonLabel: 'Delivered, not yet received',
    },
  ],
]);

/** Returns the display label for a configurable attention entity */
export function getAttentionThresholdEntityLabel(
  entity: AttentionThresholdEntity
): string {
  return ATTENTION_THRESHOLD_ENTITY_LABELS[entity];
}

/** Builds the lookup key for a configurable attention reason */
export function getAttentionThresholdSettingKey(setting: {
  entity: AttentionThresholdEntity;
  reason: AttentionReason;
}): string {
  return `${setting.entity}:${setting.reason}`;
}

/**
 * Returns the display copy for a reason, falling back to the raw reason for a
 * reason without curated copy.
 */
export function getAttentionThresholdReasonCopy(setting: {
  entity: AttentionThresholdEntity;
  reason: AttentionReason;
}): AttentionThresholdReasonCopy {
  const copy = ATTENTION_THRESHOLD_REASON_COPY.get(
    getAttentionThresholdSettingKey(setting)
  );
  if (copy !== undefined) {
    return copy;
  }
  return {
    label: `${setting.entity} ${setting.reason}`,
    reasonLabel: setting.reason,
  };
}
